<template>
  <div class="mobile-bottom-bar tw-glass-nav tw-safe-bottom lg:tw-hidden">
    <div class="mobile-bottom-bar__inner">

      <!-- Filters -->
      <button
        type="button"
        class="bottom-bar-btn tw-pressable tw-flex-1"
        :class="{ 'bottom-bar-btn--active': hasActiveFilters }"
        @click="$emit('open-filters')"
      >
        <span class="bottom-bar-btn__icon-wrap">
          <outline-icon name="filter" />
          <span
            class="tw-nav-indicator"
            :class="{ 'tw-nav-indicator--active': hasActiveFilters }"
          />
        </span>
        <span class="bottom-bar-btn__label">{{ $l('filters') }}</span>
      </button>

      <!-- Sort -->
      <div class="bottom-bar-sort tw-relative tw-flex-1" v-click-outside="closeSortSheet">
        <button
          type="button"
          class="bottom-bar-btn tw-pressable tw-w-full"
          :class="{ 'bottom-bar-btn--active': showSortSheet || sortIsActive }"
          @click.stop="toggleSortSheet"
        >
          <span class="bottom-bar-btn__icon-wrap">
            <outline-icon name="sort" />
            <span
              class="tw-nav-indicator"
              :class="{ 'tw-nav-indicator--active': sortIsActive }"
            />
          </span>
          <span class="bottom-bar-btn__label">{{ $l('sortBy') }}</span>
        </button>

        <transition name="sheet-slide">
          <div v-if="showSortSheet" class="sort-sheet tw-glass">
            <div class="sort-sheet__header">{{ $l('sortBy') }}</div>
            <button
              v-for="option in sortOptions"
              :key="option.value"
              type="button"
              class="sort-sheet__option tw-pressable"
              :class="{ 'sort-sheet__option--active': selectedSorting === option.value }"
              @click="selectSort(option.value)"
            >
              <span
                class="sort-radio"
                :class="{ 'sort-radio--active': selectedSorting === option.value }"
              >
                <span v-if="selectedSorting === option.value" class="sort-radio__dot" />
              </span>
              {{ option.label }}
            </button>
          </div>
        </transition>
      </div>

      <!-- Watchlist -->
      <div class="bottom-bar-watchlist tw-flex-1">
        <watchlist-panel bottom-bar />
      </div>

    </div>
  </div>
</template>

<script>
import OutlineIcon from './OutlineIcon.vue';
import WatchlistPanel from './WatchlistPanel.vue';
import { getWatchlist } from '@/utils/watchlist';

export default {
  name: 'MobileBottomBar',
  components: {
    OutlineIcon,
    WatchlistPanel,
  },
  props: {
    selectedSorting: {
      type: String,
      default: 'compopo-sort',
    },
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSortSheet: false,
      watchlistCount: 0,
    };
  },
  computed: {
    sortIsActive() {
      return this.selectedSorting !== 'compopo-sort';
    },
    sortOptions() {
      return [
        { value: 'compopo-sort', label: this.$l('sortCompopo') },
        { value: 'lowest-first', label: this.$l('sortLowest') },
        { value: 'highest-first', label: this.$l('sortHighest') },
      ];
    },
  },
  mounted() {
    this.refreshWatchlistCount();
    this.$root.$on('watchlist-updated', this.refreshWatchlistCount);
  },
  beforeDestroy() {
    this.$root.$off('watchlist-updated', this.refreshWatchlistCount);
  },
  methods: {
    refreshWatchlistCount() {
      this.watchlistCount = getWatchlist().length;
    },
    toggleSortSheet() {
      this.showSortSheet = !this.showSortSheet;
    },
    closeSortSheet() {
      this.showSortSheet = false;
    },
    selectSort(value) {
      this.$emit('sort', value);
      this.showSortSheet = false;
    },
  },
};
</script>

<style scoped>
.mobile-bottom-bar {
  position: fixed;
  bottom: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  z-index: 50;
}

.mobile-bottom-bar__inner {
  display: flex;
  align-items: stretch;
  min-height: 3.75rem;
}

.mobile-bottom-bar__inner > * {
  flex: 1 1 0;
  min-width: 0;
}

.bottom-bar-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  width: 100%;
  min-height: 3.75rem;
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.bottom-bar-btn__icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
}

.bottom-bar-btn--active {
  color: #2563eb;
}

.bottom-bar-btn__label {
  line-height: 1;
}

.bottom-bar-sort {
  display: flex;
  align-items: stretch;
}

/* Sort sheet */
.sort-sheet {
  position: absolute;
  bottom: calc(100% + 0.625rem);
  inset-inline-start: 0.5rem;
  inset-inline-end: 0.5rem;
  border-radius: 1.25rem;
  overflow: hidden;
  z-index: 10;
}

.sort-sheet__header {
  padding: 0.75rem 1rem;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(241, 245, 249, 0.8);
}

.sort-sheet__option {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.75rem;
  border: 0;
  background: transparent;
  padding: 0.875rem 1rem;
  font-size: 0.875rem;
  color: #334155;
  cursor: pointer;
  text-align: start;
  transition: background-color 0.15s ease;
}

.sort-sheet__option:hover {
  background: rgba(248, 250, 252, 0.8);
}

.sort-sheet__option--active {
  background: rgba(239, 246, 255, 0.9);
  font-weight: 600;
  color: #2563eb;
}

.sort-radio {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  border: 1.5px solid #cbd5e1;
}

.sort-radio--active {
  border-color: #2563eb;
  background: #2563eb;
}

.sort-radio__dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: #fff;
}

/* Watchlist cell */
.bottom-bar-watchlist {
  position: relative;
  display: flex;
  align-items: stretch;
}

.bottom-bar-watchlist ::v-deep .watchlist-panel {
  width: 100%;
  height: 100%;
}
</style>
